import React from 'react';

interface UseStickyBottomOptions {
  conversationId: string;
  dependencyKey: string;
  threshold?: number;
}

export function useStickyBottom({
  conversationId,
  dependencyKey,
  threshold = 64,
}: UseStickyBottomOptions) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const endRef = React.useRef<HTMLDivElement>(null);
  const frameRef = React.useRef<number | null>(null);
  const hasMountedRef = React.useRef(false);
  const [isStickyBottom, setIsStickyBottom] = React.useState(true);
  const [hasNewMessages, setHasNewMessages] = React.useState(false);

  const getDistanceFromBottom = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return 0;
    }

    return container.scrollHeight - container.scrollTop - container.clientHeight;
  }, []);

  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = requestAnimationFrame(() => {
      endRef.current?.scrollIntoView({ behavior, block: 'end' });
      frameRef.current = null;
    });
  }, []);

  const evaluateStickyState = React.useCallback(() => {
    const atBottom = getDistanceFromBottom() <= threshold;
    setIsStickyBottom(atBottom);
    if (atBottom) {
      setHasNewMessages(false);
    }
    return atBottom;
  }, [getDistanceFromBottom, threshold]);

  const handleScroll = React.useCallback(() => {
    evaluateStickyState();
  }, [evaluateStickyState]);

  const resumeStickyBottom = React.useCallback(() => {
    setIsStickyBottom(true);
    setHasNewMessages(false);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  React.useEffect(() => {
    setIsStickyBottom(true);
    setHasNewMessages(false);
    scrollToBottom('auto');
  }, [conversationId, scrollToBottom]);

  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (evaluateStickyState()) {
      scrollToBottom('smooth');
      return;
    }

    setHasNewMessages(true);
  }, [dependencyKey, evaluateStickyState, scrollToBottom]);

  React.useEffect(() => () => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
  }, []);

  return {
    containerRef,
    endRef,
    isStickyBottom,
    hasNewMessages,
    handleScroll,
    resumeStickyBottom,
  };
}

export default useStickyBottom;
